import { Check, FileText, Clock, ShieldCheck } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { WhitepaperForm } from "@/components/WhitepaperForm";
import { SEO } from "@/components/SEO";
import whitepaperCover from "@/assets/whitepaper-cover.png";

const articleJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "See the Game: Narrative Strategy in an Age Where Stories Replace Truth",
  "description": "A TruthForge AI white paper on narrative collapse, institutional distrust, LLM grooming, and structural foresight for legal teams, executives, and institutions.",
  "datePublished": "2026-03-01",
  "author": { "@type": "Organization", "name": "TruthForge AI" },
  "publisher": {
    "@type": "Organization",
    "name": "TruthForge AI",
    "logo": { "@type": "ImageObject", "url": "https://truthforge.ai/favicon.png" },
  },
  "image": "https://truthforge.ai/og-image.png",
  "mainEntityOfPage": "https://truthforge.ai/whitepaper",
};

const learnings = [
  "Why 2019 crisis management playbooks now actively damage reputation",
  "The four mechanisms reshaping corporate comms risk: LLM grooming, narrative velocity, deepfake doubt, and platform amplification",
  "A foresight model used by legal, comms, and reputation management leads under live pressure",
  "How to read belief formation before exposure locks your position",
];

const chapters = [
  { n: "01", title: "The Collapse of Truth Infrastructure", desc: "What broke, when, and why monitoring tools missed it." },
  { n: "02", title: "How Belief Forms Now", desc: "Frame seeding, narrative velocity, and the new physics of attention." },
  { n: "03", title: "LLM Grooming and the Poisoned Record", desc: "How models are being trained against you, in advance." },
  { n: "04", title: "Deepfakes Don't Need to Convince", desc: "The doubt economy and the cost of plausible deniability." },
  { n: "05", title: "The Foresight Model", desc: "Reading the field before exposure locks your options." },
  { n: "06", title: "What Foresight Looks Like in Practice", desc: "Frameworks and decisions for legal, comms, and the C-suite." },
];

const Whitepaper = () => (
  <div className="min-h-screen bg-background text-foreground">
    <SEO
      title="See the Game | TruthForge AI White Paper"
      description="A 32-page field manual on how belief is forming around your organisation, and how to read it before exposure locks your options. Free download."
      path="/whitepaper"
      ogType="article"
      jsonLd={articleJsonLd}
    />
    <Navigation />

    <main>
      {/* BANNER */}
      <section className="px-5 pb-4 pt-8 md:px-8 md:pb-5 md:pt-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 lg:flex-row lg:justify-center lg:gap-8">
          <div className="flex-shrink-0">
            <img
              src={whitepaperCover}
              alt="See the Game whitepaper cover"
              width={1024}
              height={1536}
              className="w-[75%] max-w-[320px] drop-shadow-[0_20px_40px_rgba(0,212,255,0.10)] lg:w-auto lg:max-w-[360px]"
            />
          </div>
          <div className="text-center lg:text-left">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">
              TruthForge White Paper · Spring 2026
            </p>
            <h1 className="mt-3 text-5xl font-black uppercase leading-[0.95] md:text-6xl lg:text-7xl">
              See the Game
            </h1>
          </div>
        </div>
      </section>

      {/* COPY + FORM */}
      <section className="px-5 pb-4 pt-4 md:px-8 md:pb-6 md:pt-5">
        <div className="mx-auto grid max-w-7xl items-start gap-8 lg:grid-cols-12 lg:gap-12">
          {/* LEFT: pitch */}
          <div className="lg:col-span-7">
            <p className="max-w-xl text-lg font-medium text-foreground/85 md:text-xl">
              The 32-page field manual on crisis management, corporate comms, and reputation management in an age where belief moves faster than fact.
            </p>

            <ul className="mt-5 space-y-3">
              {learnings.map((item) => (
                <li key={item} className="flex gap-3 text-base leading-relaxed text-body">
                  <Check className="mt-1 h-5 w-5 flex-shrink-0 text-primary" strokeWidth={2.5} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.16em] text-body">
              <span className="inline-flex items-center gap-2"><FileText className="h-3.5 w-3.5" /> 32 pages</span>
              <span className="inline-flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> 18 min read</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Confidential</span>
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-[10px] border border-border bg-surface p-6 shadow-2xl md:p-7">
                <WhitepaperForm headline="Get the white paper" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="border-y border-border/60 bg-surface/40 px-5 py-10 md:px-8 md:py-12">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">From the field</p>
          <blockquote className="mt-4 text-xl font-semibold leading-relaxed text-foreground md:text-2xl">
            “The window before exposure is the only window. Most institutions only realise it exists once it has closed. This paper is the first serious attempt to map what's happening inside that window, and what to do with it.”
          </blockquote>
          <div className="mt-4 text-xs uppercase tracking-[0.18em] text-body">
            General Counsel · FTSE 250 industrials
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="px-5 py-12 md:px-8 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">Inside the paper</p>
            <h2 className="mt-3 text-3xl font-black uppercase leading-tight md:text-4xl">Six chapters. One operating model.</h2>
            <p className="mt-3 text-base text-body md:text-lg">
              Each chapter ends with a structural takeaway you can apply the same week. No abstractions without a use.
            </p>
          </div>

          <div className="mt-8 grid gap-px overflow-hidden rounded-[10px] border border-border bg-border md:grid-cols-2">
            {chapters.map((c) => (
              <div key={c.n} className="bg-background p-6 transition hover:bg-surface/60">
                <div className="text-xs font-bold uppercase tracking-[0.24em] text-primary">Chapter {c.n}</div>
                <h3 className="mt-2 text-lg font-bold text-foreground">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-body">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUTHOR / CREDIBILITY */}
      <section className="border-t border-border/60 bg-surface/30 px-5 py-12 md:px-8 md:py-14">
        <div className="mx-auto max-w-4xl">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">Why this paper exists</p>
          <h2 className="mt-3 text-2xl font-black uppercase leading-tight md:text-3xl">
            Written for the people who carry the consequences.
          </h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-body md:text-lg">
            <p>
              TruthForge AI builds narrative intelligence for legal teams, crisis management leads, corporate comms, and reputation management functions operating under sustained pressure. This paper distils the patterns we see most often: how exposure forms, why traditional monitoring misses it, and what foresight actually looks like when belief moves faster than fact.
            </p>
            <p>
              It is not a marketing document. It is the briefing we wish more boards, GCs, and communications leads had read six months earlier.
            </p>
          </div>
        </div>
      </section>

      {/* SECONDARY CTA */}
      <section className="px-5 py-12 md:px-8 md:py-16">
        <div className="mx-auto grid max-w-5xl items-center gap-8 rounded-[12px] border border-border bg-surface p-8 shadow-2xl md:grid-cols-2 md:p-10">
          <div>
            <h2 className="text-2xl font-black uppercase leading-tight md:text-3xl">Read it before you need it.</h2>
            <p className="mt-3 text-base leading-relaxed text-body">
              The window before exposure is the only window. Get the field manual now, when you have time to think with it.
            </p>
          </div>
          <div>
            <WhitepaperForm variant="secondary" />
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default Whitepaper;
